import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
// Types
import { ToolName, ToolNameValue } from 'app/store/pagesToolbar/pagesToolbar.types';
// Store
import { useAppSelector } from 'app/hooks/useStore';
// Selectors
import { selectTrialMode } from 'app/store/AppUI/AppUI.selectors';
// Mui
import {
  Paper, Box, Toolbar, IconButton, Typography, Tooltip,
  List, ListItem, ListItemText, ListSubheader
} from '@mui/material';
// Icons
import {
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
// Utils
import { usePagesToolbarContext } from './utils/usePagesToolbarContext';

type Props = {
  onClose: () => void;
};

const excludedToolsInTrialMode:ToolNameValue[] = [
  ToolName.EpisodeLabels,
  ToolName.Colors,
  ToolName.Tags,
  ToolName.ExportPagesToPDF,
  ToolName.WorkspaceViewMode,
  ToolName.SendToAIChat
];

const SidebarToolsSettings = ({ onClose }:Props) => {
  const { t } = useTranslation();

  // State
  const trialMode = useAppSelector(selectTrialMode);

  const { tools, hiddenTools, onUpdateHiddenTools } = usePagesToolbarContext();

  const mergedTools = useMemo(() => {
    const allTools = {
      ...tools.bar,
      ...tools.additionalBar
    };
    if ( !trialMode ) return allTools;
    const toolNames = Object.keys(allTools) as ToolNameValue[];
    return toolNames.filter((toolName) => {
      return !excludedToolsInTrialMode.includes(toolName);
    });
  }, [tools, trialMode]);

  const handleClick = (toolName:ToolNameValue) => () => {
    onUpdateHiddenTools(toolName);
  }

  const toolsShown = Object.keys(mergedTools).filter((tool) => {
    return !hiddenTools.includes(tool as ToolNameValue);
  });
  const toolsHidden = Object.keys(mergedTools).filter((tool) => {
    return hiddenTools.includes(tool as ToolNameValue);
  });

  const renderListSubheader = (title:string) => (
    <ListSubheader
      sx={{
        bgcolor: '#F5F5F7',
        color: 'rgab(0,0,0,0.68)',
        fontSize: 10,
        fontWeight: 500,
        letterSpacing: '1.5px',
        textTransform: 'uppercase'
      }}
    >{title}</ListSubheader>
  )

  const renderListItem = (toolName:ToolNameValue, isHidden:boolean = false) => {
    const toolNameArray = toolName.split('_').join(' ')
    return (
      <ListItem
        key={toolName}
        secondaryAction={
          <IconButton
            onClick={handleClick(toolName)}
          >{isHidden ? <VisibilityIcon /> : <VisibilityOffIcon />}</IconButton>
        }
      ><ListItemText sx={{ textTransform: 'capitalize' }} primary={toolNameArray} /></ListItem>
    )
  };

  return (
    <Paper
      sx={{
        position: 'absolute',
        top: 0, left: 0, right: 0, bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 0,
        border: 'none',
        zIndex: 1
      }}
    >
      <Toolbar sx={{ gap: 4 }}>
        <Typography sx={{ flexGrow: 1 }}>{t('labels.toolsSettings')}</Typography>
        <Tooltip title={t('buttons.close')}>
          <IconButton
            onClick={onClose}
          ><CloseIcon /></IconButton>
        </Tooltip>
      </Toolbar>
      <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
        {toolsShown.length ? (
          <List subheader={renderListSubheader(t('labels.shown'))}>
            {toolsShown.map((tool) => renderListItem(tool as ToolNameValue))}
          </List>
        ) : null}
        {toolsHidden.length ? (
          <List subheader={renderListSubheader(t('labels.hidden'))}>
            {toolsHidden.map((tool) => renderListItem(tool as ToolNameValue, true))}
          </List>
        ) : null}
      </Box>
    </Paper>
  )
}

export default SidebarToolsSettings;
